'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Section {
  id: string;
  title: string;
  content: string[];
}

const sections: Section[] = [
  {
    id: 'intro',
    title: '1. Innledning',
    content: [
      'Velkommen til Gullklar. Disse vilkårene regulerer bruk av våre tjenester for kjøp av gull og edelmetaller.',
      'Ved å bruke våre tjenester aksepterer du disse vilkårene i sin helhet. Vennligst les nøye før du sender inn gull til oss.',
    ],
  },
  {
    id: 'company',
    title: '2. Selskapsinformasjon',
    content: [
      'Gullklar',
      'Adresse: Kristiansand',
      'E-post: kontakt@gullklar.no',
    ],
  },
  {
    id: 'services',
    title: '3. Tjenestebeskrivelse',
    content: [
      'Gullklar tilbyr kjøp av gull og edelmetaller fra privatpersoner og bedrifter i Norge.',
      'Vi kommer hjem til kunden for å evaluere kvalitet og vekt, og gir pristilbud basert på gjeldende markedspriser.',
      'Kunden har rett til å akseptere eller avslå tilbudet. Ved avslag beholder kunden gullet sitt uten kostnader.',
    ],
  },
  {
    id: 'pricing',
    title: '4. Prissetting',
    content: [
      'Priser beregnes basert på gjeldende markedspris på gull ved mottakstidspunkt, samt materialets vekt og renhet.',
      'Det benyttes en volumjustert prismodell der prisen per gram forbedres ved større innsendelser, slik at kunder som sender inn mer gull oppnår bedre betingelser.',
      'Alle priser er inklusive merverdiavgift. Det tilkommer ingen skjulte gebyrer, trekk eller administrasjonskostnader.',
    ],
  },
  {
    id: 'shipping',
    title: '5. Hjemmebesøk og gjennomføring',
    content: [
      'Gullklar tilbyr gratis hjemmebesøk for alle vurderinger.',
      'Vi avtaler tidspunkt som passer kunden.',
      'Kunden er ansvarlig for å ha gyldig legitimasjon tilgjengelig.',
      'Alle besøk gjennomføres diskret og profesjonelt.',
    ],
  },
  {
    id: 'evaluation',
    title: '6. Evaluering og tilbud',
    content: [
      'Ved hjemmebesøk gjennomføres profesjonell evaluering av vekt og renhet.',
      'Kunden mottar muntlig tilbud på stedet.',
      'Tilbudet er gyldig under besøket.',
      'Kunden kan akseptere eller avslå tilbudet uten forpliktelser.',
    ],
  },
  {
    id: 'payment',
    title: '7. Betaling',
    content: [
      'Ved aksept av tilbud utbetales beløpet umiddelbart.',
      'Betaling skjer via Vipps eller bankoverføring til oppgitt kontonummer.',
      'Kunden må oppgi gyldig norsk bankkonto og identifikasjon.',
      'Alle transaksjoner dokumenteres i henhold til hvitvaskingsloven.',
    ],
  },
  {
    id: 'return',
    title: '8. Avslag av tilbud',
    content: [
      'Ved avslag av tilbud beholder kunden gullet sitt.',
      'Ingen kostnader eller forpliktelser.',
      'Kunden står fritt til å kontakte oss igjen senere.',
    ],
  },
  {
    id: 'liability',
    title: '9. Ansvarsbegrensning',
    content: [
      'Gullklar er ansvarlig for tap eller skade på gull i vår varetekt under hjemmebesøk.',
      'Ansvar er begrenset til dokumentert verdi av gullet.',
      'Vi er ikke ansvarlige for indirekte tap eller følgeskader.',
    ],
  },
  {
    id: 'privacy',
    title: '10. Personvern',
    content: [
      'Vi behandler personopplysninger i henhold til GDPR og personopplysningsloven.',
      'Opplysninger brukes kun til gjennomføring av transaksjoner og lovpålagt dokumentasjon.',
      'Data lagres sikkert og deles ikke med tredjeparter uten samtykke.',
      'Kunden har rett til innsyn, retting og sletting av egne data.',
    ],
  },
  {
    id: 'disputes',
    title: '11. Tvister',
    content: [
      'Tvister søkes løst i minnelighet mellom partene.',
      'Ved uenighet kan saken bringes inn for Forbrukerrådet.',
      'Norsk lov gjelder for alle avtaler.',
      'Verneting er Oslo tingrett.',
    ],
  },
  {
    id: 'changes',
    title: '12. Endringer i vilkår',
    content: [
      'Gullklar forbeholder seg retten til å endre disse vilkårene.',
      'Endringer publiseres på nettsiden med varsel til registrerte kunder.',
      'Endringer trer i kraft 30 dager etter publisering.',
      'Fortsatt bruk av tjenesten etter endring innebærer aksept.',
    ],
  },
];

export default function TermsAndConditionsPage() {
  const [activeSection, setActiveSection] = useState<string>('intro');

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background shadow-md">
        <nav className="mx-auto flex h-[60px] max-w-7xl items-center justify-between px-5 lg:px-8">
          <Link href="/hjem" className="flex items-center space-x-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
              <span className="font-headline text-2xl font-bold text-primary-foreground">G</span>
            </div>
            <span className="font-headline text-xl font-semibold text-foreground lg:text-2xl">
              Gullklar
            </span>
          </Link>
          <Link
            href="/hjem"
            className="flex items-center space-x-2 font-body text-sm font-medium text-foreground transition-colors duration-250 hover:text-primary"
          >
            <Icon name="ArrowLeftIcon" size={16} variant="outline" />
            <span>Tilbake til forsiden</span>
          </Link>
        </nav>
      </header>

      <div className="h-[60px]"></div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-16">
        <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <h2 className="mb-4 font-headline text-lg font-semibold text-foreground">
                Innhold
              </h2>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full rounded-lg px-4 py-2 text-left font-body text-sm transition-colors duration-250 ${
                      activeSection === section.id
                        ? 'bg-primary/10 text-primary font-medium' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main>
            <div className="mb-8">
              <h1 className="font-headline text-3xl font-bold text-foreground lg:text-4xl">
                Vilkår og betingelser
              </h1>
              <p className="mt-3 font-body text-base text-muted-foreground">
                Sist oppdatert: 4. januar 2026
              </p>
            </div>

            <div className="space-y-8">
              {sections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="scroll-mt-24 rounded-lg bg-card p-6 shadow-sm lg:p-8"
                >
                  <h2 className="mb-4 font-headline text-xl font-semibold text-foreground lg:text-2xl">
                    {section.title}
                  </h2>
                  <div className="space-y-3">
                    {section.content.map((paragraph, index) => (
                      <p key={index} className="font-body text-base text-foreground leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {/* Contact Box */}
            <div className="mt-8 rounded-lg bg-primary/5 p-6 lg:p-8">
              <div className="flex items-start space-x-3">
                <Icon name="InformationCircleIcon" size={24} variant="solid" className="mt-1 text-primary" />
                <div>
                  <h3 className="font-headline text-lg font-semibold text-foreground">
                    Spørsmål om vilkårene?
                  </h3>
                  <p className="mt-2 font-body text-base text-muted-foreground">
                    Kontakt oss på{' '}
                    <a href="mailto:kontakt@gullklar.no" className="text-primary hover:underline">
                      kontakt@gullklar.no
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}